import {Link, useNavigate} from "react-router-dom";
import searchIcon from "../assets/icons/search-dark.png";
import React, {useState, useEffect, useRef} from "react";
import {highlight} from "../Routes/SearchListPage";
import {mapRoutesEnToSv} from "../Utils/routes";

function SearchHeroSection({content}: any) {
    const [search, setSearch] = useState('');
    const [isMobile, setIsMobile] = useState(false);
    const [showSuggestions, setShowSuggestions] = useState(false);

    const searchContainerRef = useRef<HTMLDivElement>(null);

    const navigate = useNavigate();

    useEffect(() => {
        const checkMobile = () => setIsMobile(window.innerWidth < 1280);
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (searchContainerRef.current && !searchContainerRef.current.contains(event.target as Node)) {
                setShowSuggestions(false);
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    function execSearch(e: React.FormEvent<HTMLFormElement>) {
        e.preventDefault();
        setShowSuggestions(false);
        navigate(`/sok?query=${search}`)
    }

    function handleSearchChange(e: React.ChangeEvent<HTMLInputElement>) {
        const value = e.target.value;
        setSearch(value);
        setShowSuggestions(value.length > 0);
    }

    function handleInputFocus() {
        if (search.length > 0) {
            setShowSuggestions(true);
        }
    }

    const getSuggestions = () => {
        if (!search.length || !content.searchJobsList || !Array.isArray(content.searchJobsList)) {
            return [];
        }

        // Filter the array directly
        const filtered = content.searchJobsList.filter((item: any) => {
            const nameMatches = item.name?.toLowerCase().includes(search.toLowerCase());
            let relatedMatches = false;

            if (item.related_titles && Array.isArray(item.related_titles)) {
                relatedMatches = item.related_titles.some((title: string) =>
                    title.toLowerCase().includes(search.toLowerCase())
                );
            }

            return nameMatches || relatedMatches;
        });

        return filtered.slice(0, 5); // Limit to 5 suggestions
    };


    const suggestions = getSuggestions();

    const getGradient = () => {
        return isMobile
            ? 'linear-gradient(rgba(0, 0, 0, 0.75), rgba(0, 0, 0, 0.75))'
            : 'linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5))';
    };

    return (
        <div className="w-full bg-grey-900 text-grey-300 py-16 xl:py-[100px]" style={{
            backgroundImage: content.background
                ? `${getGradient()}, url(${content.background})`
                : undefined,
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
            backgroundSize: 'cover',
        }}>
            <div className="container max-w-3xl">
                <h2 className="font-sweing text-5xl xl:text-7xl font-medium mb-6 text-grey-100">
                    {content.preTitle && content.preTitle.length &&
                        <span className="w-full text-3xl xl:text-5xl">{content.preTitle}<br/></span>}
                    {content.title}
                </h2>

                {content.text && content.text.length && <p className="font-body xl:text-xl mb-6">{content.text}</p>}

                <div className="relative mb-4" ref={searchContainerRef}>
                    <form onSubmit={execSearch} id="search-form">
                        <input
                            type="text"
                            placeholder={content.searchPlaceholder}
                            value={search}
                            onChange={handleSearchChange}
                            onFocus={handleInputFocus}
                            className="w-full ps-5 pe-12 py-5 font-body xl:text-xl bg-grey-300 text-grey-900 placeholder-grey-700 rounded focus:outline-none"
                        />
                        <button type="submit" className="absolute z-10 top-1/2 -translate-y-1/2 right-4">
                            <img src={searchIcon} width={24} height={24} alt="Search"/>
                        </button>
                    </form>

                    {showSuggestions && suggestions.length > 0 && (
                        <div className="absolute top-full left-0 right-0 z-20 bg-grey-300 mt-1 rounded shadow-lg max-h-[50vh] overflow-y-auto dropdown-scroller">
                            <p className="px-3 pt-4 pb-1 text-sm font-body text-grey-500 uppercase">
                                Sökförslag
                            </p>
                            {suggestions.map((suggestion: any, index: number) => {
                                let matchedRelatedTitles: string[] = [];

                                if (search.length > 0 && suggestion.related_titles) {
                                    matchedRelatedTitles = suggestion.related_titles.filter((title: string) =>
                                        title.toLowerCase().includes(search.toLowerCase())
                                    );
                                }

                                return (
                                    <Link
                                        key={suggestion.id}
                                        to={`/${mapRoutesEnToSv(suggestion.type)}/${suggestion.id}/${suggestion.slug}`}
                                        className="block px-3 py-2 border-b border-grey-400 last:border-b-0 cursor-pointer"
                                        onClick={(e) => {setSearch(''); setShowSuggestions(false)}}
                                    >
                                        <div className="text-sm text-grey-900 font-body">
                                            <span
                                                dangerouslySetInnerHTML={{__html: highlight(suggestion.name || '', search)}}/>
                                            {matchedRelatedTitles.length > 0 && (
                                                <p className="text-xs text-grey-600 mt-1"
                                                   dangerouslySetInnerHTML={{__html: highlight(matchedRelatedTitles.join(', '), search)}}/>
                                            )}
                                        </div>
                                    </Link>
                                );
                            })}
                        </div>
                    )}

                </div>

                <div className="flex flex-col xl:flex-row gap-2.5 overflow-x-auto items-start xl:items-center">
                    <p className="whitespace-nowrap flex-shrink-0 text-sm">{content.recentJobsText}</p>
                    <div className="flex flex-row gap-2 text-sm flex-wrap xl:flex-nowrap min-w-0 overflow-x-auto text-grey-100">
                        {content.recentJobs?.map((job: any, index: number) => (
                            <Link
                                to={job.url}
                                key={index}
                                className="flex items-center justify-center py-2.5 px-4 border-grey-100 border min-w-[6rem] xl:max-w-[12rem] xl:truncate whitespace-nowrap xl:overflow-hidden"
                            >
                                <span className="xl:truncate">{job.title}</span>
                            </Link>
                        ))}
                    </div>
                </div>

            </div>
        </div>
    )
}

export default SearchHeroSection;